import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Package, Clock, AlertCircle, MessageCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { WhatsAppButton, WhatsAppMessages } from '../components/WhatsAppChat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const reference = searchParams.get('reference') || searchParams.get('trxref') || searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  
  const [status, setStatus] = useState('checking');
  const [order, setOrder] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (reference) {
      pollPaymentStatus();
    }
  }, [reference]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/verify/${reference}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        if (orderId) {
          fetchOrder();
        }
        return;
      } else if (response.data.status === 'expired' || response.data.status === 'failed') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Failed to fetch order:', error);
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <div className="text-center">
            <div className="spinner mx-auto mb-6"></div>
            <h1 className="text-2xl font-bold text-stone-900 mb-2">Processing Payment</h1>
            <p className="text-stone-600">Please wait while we confirm your payment...</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-stone-900 mb-2" data-testid="success-title">
              Payment Successful!
            </h1>
            <p className="text-stone-600 mb-6">
              Thank you for your order. Your payment has been confirmed and your order is being processed.
            </p>

            {order && (
              <div className="bg-stone-50 rounded-xl p-6 text-left mb-6">
                <h3 className="font-semibold text-stone-900 mb-4">Order Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-stone-600">Order ID:</span>
                    <span className="font-mono text-stone-900">{order.id.slice(0, 8)}...</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-600">Status:</span>
                    <span className="badge-success">{order.status}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-600">Items:</span>
                    <span className="text-stone-900">{order.items.length} items</span>
                  </div>
                  <div className="flex justify-between border-t border-stone-200 pt-2 mt-2">
                    <span className="font-semibold text-stone-900">Total:</span>
                    <span className="font-bold text-green-900">R{order.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 mb-6 text-left">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-yellow-800">Payment Held</h4>
                  <p className="text-sm text-yellow-700">
                    Your payment is being held until your order is fulfilled. 
                    If any items are out of stock, you'll receive a refund for those items.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/orders">
                <Button className="btn-primary" data-testid="view-orders-btn">
                  <Package className="w-5 h-5 mr-2" />
                  View My Orders
                </Button>
              </Link>
              <Link to="/products">
                <Button variant="outline" data-testid="continue-shopping-btn">
                  Continue Shopping
                </Button>
              </Link>
            </div>

            {/* WhatsApp Chat for Order Confirmation */}
            {order && (
              <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-700 mb-3">
                  Need help with your order? Chat with us on WhatsApp!
                </p>
                <WhatsAppButton
                  message={WhatsAppMessages.orderConfirmation(order.id.slice(0, 8), order.total.toFixed(2))}
                  label="Chat About This Order"
                  className="w-full sm:w-auto"
                />
              </div>
            )}
          </div>
        );

      case 'expired':
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-12 h-12 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-stone-900 mb-2">Payment Expired</h1>
            <p className="text-stone-600 mb-6">
              Your payment session has expired. Please try again.
            </p>
            <Link to="/cart">
              <Button className="btn-primary">Return to Cart</Button>
            </Link>
          </div>
        );

      case 'timeout':
      case 'error':
        return (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-12 h-12 text-yellow-600" />
            </div>
            <h1 className="text-2xl font-bold text-stone-900 mb-2">Payment Status Unknown</h1>
            <p className="text-stone-600 mb-6">
              We couldn't confirm your payment status. Please check your orders page or contact support.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/orders">
                <Button className="btn-primary">Check My Orders</Button>
              </Link>
              <Link to="/">
                <Button variant="outline">Go Home</Button>
              </Link>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-stone-50" data-testid="order-success-page">
      <Navbar />
      
      <main className="flex-1 flex items-center justify-center">
        <div className="max-w-lg mx-auto px-4 py-16">
          <div className="bg-white rounded-2xl p-8 shadow-sm">
            {renderContent()}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default OrderSuccessPage;
